import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Canvas, Object3DProps, ThreeEvent, useLoader, useThree } from '@react-three/fiber';
import { CameraControls, Center, Environment, Html, useProgress, Line } from '@react-three/drei';
import { useDispatch, useSelector } from 'react-redux';
import {
  ACESFilmicToneMapping,
  Group,
  Object3D,
  SRGBColorSpace,
  Vector2,
  Vector3,
  DoubleSide,
  Box3,
  PerspectiveCamera,
  MeshStandardMaterial,
  Mesh,
} from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { ContainerItem } from '../Types/ContainerItem';
import BaseContainerData from '../Types/BaseContainerData';
import CircularProgress from '@material-ui/core/CircularProgress';
import { HtmlProps } from '@react-three/drei/web/Html';
import { setSelectedContainerItem } from '../Reducers/Configurator/Actions';
import { getSelectedFloor } from '../Reducers/Configurator/Selectors';
import { useScreenshotRegistry } from './ScreenshotContext';

const ContainerHeight = 2.592;
const ContainerWidth = 2.485;
const ContainerLength = 6.074;
const AisleLength = 2.655;

const enum ContainerParts {
  container = 'container',
  aisle = 'aisle',
  stairs = 'stairs',
  railing = 'railing',
}

interface DimensionLineProps {
  start: Vector3;
  end: Vector3;
  direction: Vector3;
  distance: number;
  label: string;
  occludeRef: React.RefObject<Group>;
}

const DimensionLine: React.FC<DimensionLineProps> = ({
  start,
  end,
  direction,
  distance,
  label,
  occludeRef,
}) => {
  const offsetVec = direction.clone().normalize().multiplyScalar(distance);
  const dimStart = start.clone().add(offsetVec);
  const dimEnd = end.clone().add(offsetVec);
  const mid = dimStart.clone().add(dimEnd).multiplyScalar(0.5);

  const lineDir = dimEnd.clone().sub(dimStart).normalize();
  const up = new Vector3(0, 1, 0);
  const ref = Math.abs(lineDir.y) > 0.9 ? new Vector3(1, 0, 0) : up;
  const tickVec = new Vector3().crossVectors(lineDir, ref).normalize().multiplyScalar(0.075);

  const mainColor = '#f44336';
  const anchorColor = '#f44336';

  return (
    <group userData={{ isDimension: true }}>
      <Line points={[start, dimStart]} color={anchorColor} lineWidth={0.5} transparent opacity={0.4} />
      <Line points={[end, dimEnd]} color={anchorColor} lineWidth={0.5} transparent opacity={0.4} />
      <Line points={[dimStart, dimEnd]} color={mainColor} lineWidth={2} />
      <Line
        points={[dimStart.clone().sub(tickVec), dimStart.clone().add(tickVec)]}
        color={mainColor}
        lineWidth={2}
      />
      <Line
        points={[dimEnd.clone().sub(tickVec), dimEnd.clone().add(tickVec)]}
        color={mainColor}
        lineWidth={2}
      />
      <Html
        position={mid}
        center
        occlude={occludeRef ? [occludeRef] : undefined}
        transform={false}
        zIndexRange={[100, 0]}
        style={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            background: '#f44336',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            marginBottom: '4px',
          }}
        >
          {label}
        </div>
      </Html>
    </group>
  );
};

interface DimensionsProps {
  visible: boolean;
  targetGroup: React.RefObject<Group>;
  trigger: ContainerItem[] | number[];
  hideHeight: boolean;
  isLoading: boolean;
}

const Dimensions: React.FC<DimensionsProps> = ({ visible, targetGroup, trigger, hideHeight, isLoading }) => {
  const [bounds, setBounds] = useState<{ min: Vector3; max: Vector3 } | null>(null);

  useLayoutEffect(() => {
    if (visible && targetGroup.current && !isLoading) {
      const timer = setTimeout(() => {
        if (!targetGroup.current) return;

        const box = new Box3();
        targetGroup.current.traverse((obj) => {
          if (
            obj instanceof Mesh &&
            obj.visible &&
            !obj.userData.isDimension &&
            !obj.parent?.userData.isDimension
          ) {
            box.expandByObject(obj);
          }
        });

        if (!box.isEmpty()) {
          const min = box.min.clone();
          const max = box.max.clone();
          targetGroup.current.worldToLocal(min);
          targetGroup.current.worldToLocal(max);
          setBounds({ min, max });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [visible, targetGroup, trigger, isLoading]);

  if (!visible || !bounds) return null;

  const { min, max } = bounds;
  const width = max.x - min.x;
  const depth = max.z - min.z;
  const height = max.y - min.y;

  const padding = 0.2;

  return (
    <group>
      <DimensionLine
        start={new Vector3(min.x, min.y, max.z)}
        end={new Vector3(max.x, min.y, max.z)}
        direction={new Vector3(0, 0, 1)}
        distance={padding}
        label={`${width.toFixed(2)}m`}
        occludeRef={targetGroup}
      />
      <DimensionLine
        start={new Vector3(max.x, min.y, max.z)}
        end={new Vector3(max.x, min.y, min.z)}
        direction={new Vector3(1, 0, 0)}
        distance={padding}
        label={`${depth.toFixed(2)}m`}
        occludeRef={targetGroup}
      />
      {!hideHeight && (
        <DimensionLine
          start={new Vector3(max.x, min.y, min.z)}
          end={new Vector3(max.x, max.y, min.z)}
          direction={new Vector3(1, 0, -1)}
          distance={padding * 2}
          label={`${height.toFixed(2)}m`}
          occludeRef={targetGroup}
        />
      )}
    </group>
  );
};

const DimensionToggle: React.FC<{ active: boolean; onClick: () => void }> = ({ active, onClick }) => {
  const iconSrc =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMjQgMjRWMTA0SDMwVjEwMEgzMlYxMDRIMzhWOTRINDBWMTA0SDQ2VjEwMEg0OFYxMDRINTRWOTRINTZWMTA0SDYyVjEwMEg2NFYxMDRINzBWOTRINzJWMTA0SDc4VjEwMEg4MFYxMDRIODZWOTRIODhWMTA0SDEwNEwyNCAyNFpNNDAgODhINjRMNDAgNjRWODhaIiBmaWxsPSIjMEQxMzE5Ii8+Cjwvc3ZnPgo=';
  const portalTarget = document.getElementById('dimension-toggle-root');

  if (!portalTarget) return null;

  return createPortal(
    <div
      onClick={onClick}
      style={{
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 0,
        position: 'unset',
      }}
      className="jss59 jss61 jss69"
    >
      <img
        src={iconSrc}
        alt="Toggle Dimensions"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          opacity: active ? 1 : 0.5,
        }}
      />
    </div>,
    portalTarget
  );
};

interface GroundProps {
  baseContainer: BaseContainerData;
}

const ContainerGround: React.FC<GroundProps> = ({ baseContainer }) => {
  const x = baseContainer.rows * ContainerLength + (baseContainer.rows - 1) * AisleLength + 4;
  const z = baseContainer.columns * ContainerWidth + 4;
  return (
    <mesh name="ground" position={[0, -0.01, 0]} rotation-x={-Math.PI / 2} frustumCulled={false}>
      <planeGeometry args={[x, z]} />
      <meshStandardMaterial color="#E9E9E9" />
    </mesh>
  );
};

interface LoaderProps extends HtmlProps {
  onUnmount?: () => void;
  children?: ReactNode;
  hideSpinner?: boolean;
}

const Loader: React.FC<LoaderProps> = ({ onUnmount, hideSpinner, children, ...htmlProps }) => {
  useEffect(() => {
    return onUnmount;
  }, [onUnmount]);
  return (
    <>
      {children}
      {!hideSpinner && (
        <Html center {...htmlProps}>
          <CircularProgress color={'inherit'} />
        </Html>
      )}
    </>
  );
};

interface GltfModelProps extends Object3DProps {
  url: string;
  leftWallVisible?: boolean;
  rightWallVisible?: boolean;
}

const GltfModel: React.FC<GltfModelProps> = React.forwardRef<Group, GltfModelProps>(
  ({ url, leftWallVisible, rightWallVisible, ...props }, ref) => {
    const gltf = useLoader(GLTFLoader, url);
    const model = useMemo(() => {
      const clone = gltf.scene.clone(true);
      clone.traverse((child: Object3D) => {
        if (child.name === 'wallleft' && leftWallVisible !== undefined) {
          child.visible = leftWallVisible;
        }
        if (child.name === 'wallright' && rightWallVisible !== undefined) {
          child.visible = rightWallVisible;
        }
      });

      return clone;
    }, [gltf.scene, leftWallVisible, rightWallVisible]);

    return <primitive object={model} {...props} ref={ref} />;
  }
);

const GltfModelWithLoader: React.FC<GltfModelProps> = (props) => {
  const propsRef = useRef(props);
  propsRef.current = props;

  const lastLoadedUrl = useRef(props.url);
  const updateFallbackModel = useCallback(() => {
    lastLoadedUrl.current = propsRef.current.url;

    setFallbackModel(
      <Loader position-y={ContainerHeight / 2} onUnmount={updateFallbackModel}>
        <GltfModel {...propsRef.current} />
      </Loader>
    );
  }, []);

  const [fallbackModel, setFallbackModel] = useState(
    <Loader position-y={ContainerHeight / 2} onUnmount={updateFallbackModel}>
      <GltfModel {...props} />
    </Loader>
  );

  useEffect(() => {
    if (props.url === lastLoadedUrl.current) {
      updateFallbackModel();
    }
  }, [props.leftWallVisible, props.rightWallVisible, props.url, updateFallbackModel]);

  return (
    <Suspense fallback={fallbackModel}>
      <GltfModel {...props} />
    </Suspense>
  );
};

interface ContainerModelProps {
  containerData: ContainerItem;
  visible: boolean;
}

interface ContainerModelProps {
  containerData: ContainerItem;
  visible: boolean;
}

const ContainerModel: React.FC<ContainerModelProps> = (props) => {
  const itemIdentifier = useSelector((state) => state.configurator?.configuration?.item?.identifier || '');
  const selectedContainerItem = useSelector((state) => state.configurator?.selectedContainerItem);
  const selectedFloor = useSelector(getSelectedFloor);

  const name = props.containerData.selectedOption;
  const { containerData } = props;
  const dispatch = useDispatch();

  const handleContainerClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      if (containerData.floor !== selectedFloor) return;

      dispatch(setSelectedContainerItem(containerData));
    },
    [dispatch, containerData, selectedFloor]
  );

  const isSelected =
    selectedContainerItem &&
    selectedContainerItem.floor === containerData.floor &&
    selectedContainerItem.row === containerData.row &&
    selectedContainerItem.column === containerData.column;

  if (!itemIdentifier) {
    return null;
  }

  const y = containerData.floor * ContainerHeight;
  const x = containerData.row * (ContainerLength + AisleLength);
  const z = -containerData.column * ContainerWidth + (containerData.row === 1 ? 0.007 : 0);
  const rotY = containerData.row === 1 ? -Math.PI : 0;

  return (
    <group position-x={x} position-y={y} position-z={z} rotation-y={rotY} visible={props.visible}>
      <GltfModelWithLoader
        url={`${getConf('network.host')}/visualization_component/${itemIdentifier}/${ContainerParts.container}/${name}/${name}.gltf`}
        onClick={props.visible ? handleContainerClick : undefined}
        leftWallVisible={containerData.leftWall}
        rightWallVisible={containerData.rightWall}
      />
      {isSelected && (
        <mesh rotation-x={-Math.PI / 2} position={[0, 0.15, 0]}>
          <planeGeometry args={[ContainerLength - 0.1, ContainerWidth - 0.1]} />

          <meshBasicMaterial color="#FFD000" transparent opacity={0.5} side={DoubleSide} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
};

interface AisleModelProps {
  containerData: ContainerItem;
  baseContainer: BaseContainerData;
  visible: boolean;
}

const AisleModel: React.FC<AisleModelProps> = (props) => {
  const itemIdentifier = useSelector((state) => state.configurator?.configuration?.item?.identifier || '');
  const { containerData, baseContainer } = props;
  const { columns } = baseContainer;
  const lastColumn = columns - 1;

  const isFront = containerData.column === 0;
  const isBack = containerData.column === lastColumn;

  const name = columns === 1 ? 'aisle' : isFront ? 'aisle_front' : isBack ? 'aisle_back' : 'aisle_middle';

  if (!itemIdentifier) {
    return null;
  }

  const y = props.containerData.floor * ContainerHeight;
  const x = isFront || isBack ? 0 : ContainerLength + AisleLength;
  const z = -props.containerData.column * ContainerWidth;
  const rotY = isFront || isBack ? 0 : Math.PI;

  return (
    <group position={[x, y, z]} rotation={[0, rotY, 0]} visible={props.visible}>
      <GltfModelWithLoader
        url={`${getConf('network.host')}/visualization_component/${itemIdentifier}/${ContainerParts.aisle}/${name}/${name}.gltf`}
      />
    </group>
  );
};

interface StairModel {
  containerData: ContainerItem;
  baseContainer: BaseContainerData;
  visible: boolean;
  floors: number;
}

const StairModel: React.FC<StairModel> = (props) => {
  const { containerData, baseContainer, floors } = props;
  const { rows } = baseContainer;
  const itemIdentifier = useSelector((state) => state.configurator?.configuration?.item?.identifier || '');

  const name =
    floors === 2
      ? rows === 1
        ? 'first_floor_one_row'
        : 'first_floor_two_rows'
      : rows === 1
        ? 'second_floor_one_row'
        : 'second_floor_two_rows';

  const y = containerData.floor * ContainerHeight;
  const x = containerData.row * (ContainerLength + AisleLength);
  const z = -containerData.column * ContainerWidth;
  return (
    <group position={[x, y, z]} visible={props.visible}>
      <GltfModelWithLoader
        url={`${getConf('network.host')}/visualization_component/${itemIdentifier}/${ContainerParts.stairs}/${name}/${name}.gltf`}
      />
    </group>
  );
};

interface RailingModel {
  containerData: ContainerItem;
  baseContainer: BaseContainerData;
  isBack: boolean;
  visible: boolean;
}

const RailingModel: React.FC<RailingModel> = (props) => {
  const { containerData, baseContainer, isBack, visible } = props;
  const name = isBack ? 'railing_back' : 'railing_middle';
  const itemIdentifier = useSelector((state) => state.configurator?.configuration?.item?.identifier || '');
  const y = containerData.floor * ContainerHeight;
  const x = containerData.row * (ContainerLength + AisleLength);
  const z = -containerData.column * ContainerWidth;
  if (baseContainer.rows > 1) return null;
  return (
    <group position={[x, y, z]} visible={visible}>
      <GltfModelWithLoader
        url={`${getConf('network.host')}/visualization_component/${itemIdentifier}/${ContainerParts.railing}/${name}/${name}.gltf`}
      />
    </group>
  );
};
const ScreenshotHandler: React.FC<{
  rows: number;
  floors: number;
  columns: number;
}> = ({ rows, floors, columns }) => {
  const { gl, scene, camera: mainCamera } = useThree();
  const { register, unregister } = useScreenshotRegistry();

  const screenshotCamera = useMemo(() => {
    const TARGET_W = 1024;
    const TARGET_H = 768;
    const cam = new PerspectiveCamera(50, TARGET_W / TARGET_H, 0.1, 1000);
    const fixedRows = Math.max(rows, 2);
    const fixedFloorIndex = Math.max(0, floors - 1);
    const idealSceneCenter = new Vector3(0, (floors * ContainerHeight) / 2, 0);

    const defaultShadowBase = new Vector3(
      (columns * ContainerWidth) / 4 + (fixedFloorIndex + 1) * ContainerHeight,
      idealSceneCenter.y + 1 + (fixedRows * ContainerLength) / 4,
      (fixedRows * ContainerLength) / 2 + ((fixedFloorIndex + 1) * ContainerHeight) / 2
    ).normalize();

    const x = fixedRows * (ContainerLength + AisleLength);
    const y = floors * ContainerHeight;
    const z = columns * ContainerWidth;

    const radius = new Vector3(x, y, z).length() / 2;
    const fov = 50 * (Math.PI / 180);
    const fitDistance = (radius / Math.sin(fov / 2)) * 1.3;

    const pos = idealSceneCenter.clone().add(defaultShadowBase.multiplyScalar(fitDistance));
    cam.position.copy(pos);
    cam.lookAt(idealSceneCenter);

    return cam;
  }, [rows, floors, columns]);

  const performScreenshot = useCallback(() => {
    if (!gl || !scene || !mainCamera) return null;

    const originalSize = new Vector2();
    gl.getSize(originalSize);
    gl.setSize(1024, 768, false);

    const originalState = new Map<Object3D, { visible?: boolean; color?: number }>();

    const ground = scene.getObjectByName('ground');
    if (ground && ground instanceof Mesh && ground.material instanceof MeshStandardMaterial) {
      originalState.set(ground, { color: ground.material.color.getHex() });
      ground.material.color.setHex(0xc5c0bc);
    }

    scene.traverse((object) => {
      if (object.name === 'wallleft' || object.name === 'wallright') {
        return;
      }
      const isDimension = object.userData.isDimension || object.parent?.userData.isDimension;

      if (isDimension) {
        if (object.visible) {
          originalState.set(object, { visible: true });
          object.visible = false;
        }
      } else if (object.visible === false) {
        originalState.set(object, { visible: false });
        object.visible = true;
      }
    });

    screenshotCamera.updateMatrixWorld();
    gl.render(scene, screenshotCamera);
    const base64 = gl.domElement.toDataURL('image/png');

    gl.setSize(originalSize.x, originalSize.y, false); // Restore resolution

    originalState.forEach((state, object) => {
      if (state.visible !== undefined) object.visible = state.visible;
      if (
        state.color !== undefined &&
        object instanceof Mesh &&
        object.material instanceof MeshStandardMaterial
      ) {
        object.material.color.setHex(state.color);
      }
    });

    gl.render(scene, mainCamera);

    return base64;
  }, [gl, scene, mainCamera, screenshotCamera]);

  useEffect(() => {
    register(performScreenshot);
    return () => unregister();
  }, [register, unregister, performScreenshot]);

  return null;
};

export const Visualization: React.FC = () => {
  const containers: ContainerItem[] = useSelector(
    (state) => state.configurator?.configuration?.containers || []
  );
  const baseContainer = useSelector(
    (state) => state.configurator?.configuration?.customdata?.baseContainer || {}
  );
  const { floors = 1, rows = 1, columns = 1 } = baseContainer;

  const [showDimensions, setShowDimensions] = useState(true);
  const containerGroupRef = useRef<Group>(null);

  const selectedFloor = useSelector(getSelectedFloor);
  const selectedContainerItem = useSelector((state) => state.configurator?.selectedContainerItem);
  const dispatch = useDispatch();
  const isPartialView = selectedFloor < floors - 1;

  useEffect(() => {
    if (selectedContainerItem && selectedContainerItem.floor !== selectedFloor) {
      dispatch(setSelectedContainerItem(null));
    }
  }, [selectedFloor, selectedContainerItem, dispatch]);

  const sceneCenter = useMemo(() => {
    return new Vector3(0, (selectedFloor + 0.5) * ContainerHeight, 0);
  }, [selectedFloor]);
  const defaultCameraPositionBase = useMemo(() => {
    return new Vector3(
      (columns * ContainerWidth) / 4 + (selectedFloor + 1) * ContainerHeight,
      sceneCenter.y + 1 + (rows * ContainerLength) / 4,
      (rows * ContainerLength) / 2 + ((selectedFloor + 1) * ContainerHeight) / 2
    ).normalize();
  }, [sceneCenter, columns, rows, selectedFloor]);
  const initialCameraPosition = useMemo(() => {
    const y = floors * ContainerHeight;
    const x = rows * (ContainerLength + AisleLength);
    const z = columns * ContainerWidth;

    const radius = new Vector3(x, y, z).length() / 2;
    const fov = 50 * (Math.PI / 180);
    const fitDistance = (radius / Math.sin(fov / 2)) * 1.3;

    return sceneCenter.clone().add(defaultCameraPositionBase.clone().multiplyScalar(fitDistance));
  }, [floors, rows, columns, defaultCameraPositionBase, sceneCenter]);

  const cameraControlsRef = useRef<CameraControls>();

  const { active } = useProgress();

  const [cacheKey, setCacheKey] = useState(0);
  useEffect(() => {
    if (!active) {
      setCacheKey((cacheKey) => cacheKey + 1);
    }
  }, [active]);

  const [firstSceneRendered, setFirstSceneRendered] = useState(false);

  useEffect(() => {
    if (!firstSceneRendered) {
      return;
    }

    const cameraControls = cameraControlsRef.current;
    if (!cameraControls) {
      return;
    }
    const position = cameraControls
      .getPosition(new Vector3())
      .sub(cameraControls.getTarget(new Vector3()))
      .add(sceneCenter);
    cameraControls.setLookAt(
      position.x,
      position.y,
      position.z,
      sceneCenter.x,
      sceneCenter.y,
      sceneCenter.z,
      true
    );
  }, [sceneCenter, firstSceneRendered]);

  const boundingSphereRadius = useRef();
  const handleCentered = useCallback(
    ({ boundingSphere }) => {
      if (active || boundingSphere.isEmpty()) {
        return;
      }
      if (!firstSceneRendered) {
        setFirstSceneRendered(true);
      }

      if (
        boundingSphereRadius.current &&
        Math.abs(boundingSphere.radius - boundingSphereRadius.current) < 1
      ) {
        return;
      }
      boundingSphereRadius.current = boundingSphere.radius;

      const cameraControls = cameraControlsRef.current;
      if (!cameraControls) {
        return;
      }
      cameraControls.setLookAt(
        initialCameraPosition.x,
        initialCameraPosition.y,
        initialCameraPosition.z,
        sceneCenter.x,
        sceneCenter.y,
        sceneCenter.z,
        true
      );
    },
    [initialCameraPosition, sceneCenter, firstSceneRendered, active]
  );

  const lastColumn = columns - 1;

  const stairPositions =
    floors > 1 ? containers.filter((c) => c.column === 0 && c.row === 0 && c.floor === 0) : [];

  const hasContainers = containers.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <DimensionToggle active={showDimensions} onClick={() => setShowDimensions(!showDimensions)} />
      <Canvas
        camera={{
          position: initialCameraPosition,
          fov: 50,
          near: 0.1,
          far: 1000,
        }}
        shadows={false}
        gl={{
          antialias: true,
          logarithmicDepthBuffer: true,
          preserveDrawingBuffer: true, // Needed for snapshotting
        }}
        onCreated={({ gl }) => {
          gl.toneMapping = ACESFilmicToneMapping;
          gl.outputColorSpace = SRGBColorSpace;
        }}
      >
        <ScreenshotHandler rows={rows} floors={floors} columns={columns} />
        <CameraControls
          ref={cameraControlsRef}
          minDistance={2}
          maxDistance={50}
          maxPolarAngle={Math.PI / 2}
        />

        <Environment preset="city" />

        {hasContainers && <ContainerGround baseContainer={baseContainer} />}

        {!firstSceneRendered && <Loader />}

        <Center disableY onCentered={handleCentered} cacheKey={cacheKey}>
          <group visible={firstSceneRendered}>
            <Dimensions
              visible={showDimensions}
              targetGroup={containerGroupRef}
              trigger={[containers, selectedFloor]}
              hideHeight={isPartialView}
              isLoading={active}
            />
            <group ref={containerGroupRef}>
              {containers.map((containerItem) => (
                <React.Fragment key={`${containerItem.floor}-${containerItem.row}-${containerItem.column}`}>
                  <ContainerModel
                    containerData={containerItem}
                    visible={containerItem.floor <= selectedFloor}
                  />
                  {rows > 1 && containerItem.row === 0 && (
                    <AisleModel
                      baseContainer={baseContainer}
                      containerData={containerItem}
                      visible={containerItem.floor <= selectedFloor}
                    />
                  )}
                  {floors > 1 && rows === 1 && containerItem.floor < floors - 1 && (
                    <RailingModel
                      containerData={containerItem}
                      baseContainer={baseContainer}
                      isBack={containerItem.column === lastColumn}
                      visible={containerItem.floor <= selectedFloor}
                    />
                  )}
                </React.Fragment>
              ))}
              {stairPositions.map((containerItem) => (
                <StairModel
                  key={`stair-${containerItem.floor}-${containerItem.row}`}
                  containerData={containerItem}
                  baseContainer={baseContainer}
                  visible={containerItem.floor <= selectedFloor}
                  floors={floors}
                />
              ))}
            </group>
          </group>
        </Center>
      </Canvas>
    </div>
  );
};
